import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { allContent, siteInfo } from '@/mocks/movies';
import type { MovieItem } from '@/mocks/movies';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<MovieItem | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const found = allContent.find((item) => item.id === id);
    setArticle(found || null);
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="px-4 md:px-6 max-w-7xl mx-auto py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-emotion-sad-line text-foreground-400 text-3xl"></i>
            </div>
            <h2 className="text-foreground-900 text-xl font-bold mb-2">内容未找到</h2>
            <p className="text-foreground-500 mb-6">该内容可能已被移除或链接失效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryLabel = article.category === 'movie' ? '电影' : article.category === 'tv' ? '电视剧' : '综艺';
  const categoryPath = `/category/${article.category}`;

  const relatedItems = allContent
    .filter((item) => item.id !== article.id && item.category === article.category)
    .slice(0, 6);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    author: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    datePublished: article.year,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
      url: siteInfo.url,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteInfo.url}/article/${article.id}`,
    },
  };

  const breadcrumbItems = [
    { label: '首页', path: '/' },
    { label: categoryLabel, path: categoryPath },
    { label: article.title },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="px-4 md:px-6 max-w-5xl mx-auto">
            {/* Breadcrumb */}
            <div className="py-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            {/* Article header */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 py-6">
              {/* Poster */}
              <div className="md:col-span-1">
                <div className="relative overflow-hidden rounded-xl aspect-[2/3]">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-3 right-3 bg-primary-500 text-background-50 text-sm font-bold px-3 py-1.5 rounded-lg">
                    {article.rating}
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className="md:col-span-2">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3 font-heading">
                  {article.title}
                </h1>
                <p className="text-foreground-600 text-sm leading-relaxed mb-5">
                  {article.description}
                </p>

                <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm mb-6">
                  <div className="flex items-center gap-2">
                    <span className="text-foreground-500">导演：</span>
                    <span className="text-foreground-800">{article.director}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground-500">年份：</span>
                    <span className="text-foreground-800">{article.year}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground-500">地区：</span>
                    <span className="text-foreground-800">{article.region}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground-500">时长：</span>
                    <span className="text-foreground-800">{article.duration}</span>
                  </div>
                  <div className="flex items-center gap-2 col-span-2">
                    <span className="text-foreground-500">主演：</span>
                    <span className="text-foreground-800">{article.actors.join(' · ')}</span>
                  </div>
                  <div className="flex items-center gap-2 col-span-2">
                    <span className="text-foreground-500">类型：</span>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {article.tags.map((tag) => (
                        <span key={tag} className="text-xs px-2.5 py-0.5 bg-background-100 text-foreground-600 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 col-span-2">
                    <span className="text-foreground-500">更新：</span>
                    <span className="text-accent-500">{article.updateInfo}</span>
                  </div>
                </div>

                {/* Play button */}
                <button className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 font-semibold rounded-full hover:bg-primary-600 transition-all duration-300 transform hover:scale-105 cursor-pointer whitespace-nowrap">
                  <i className="ri-play-circle-line text-lg"></i>
                  立即播放
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="py-8 border-t border-background-300/60">
              <h2 className="text-xl font-bold text-foreground-950 mb-4 font-heading">剧情简介</h2>
              <div className="text-foreground-600 leading-relaxed text-sm space-y-3">
                {article.content.split('\n').map((para, idx) => (
                  <p key={idx}>{para}</p>
                ))}
              </div>
            </div>

            {/* Related */}
            {relatedItems.length > 0 && (
              <section className="py-8 border-t border-background-300/60">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                  <h2 className="text-xl font-bold text-foreground-950 font-heading">
                    相关推荐
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
                  {relatedItems.map((item) => (
                    <MovieCard key={item.id} item={item} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}